import React, { ReactNode } from 'react';
import Select from 'react-select';
import { t } from '@lingui/core/macro';
import { Input } from 'src/components/ui';
import Field, { describedById } from './Field';
import { FieldWidth, SelectOption } from './types';

export type SelectFieldProps = {
  id: string;
  label: ReactNode;
  value: string | number | null | undefined;
  options: SelectOption[];
  onChange: (value: string | number | null) => void;
  required?: boolean;
  error?: string;
  help?: ReactNode;
  width?: FieldWidth;
  placeholder?: string;
  loading?: boolean;
  searchable?: boolean;
  disabled?: boolean;
};

export function SelectField({
  id,
  label,
  value,
  options,
  onChange,
  required,
  error,
  help,
  width,
  placeholder,
  loading,
  searchable,
  disabled,
}: SelectFieldProps) {
  const selected = options.find(option => String(option.value) === String(value)) ?? null;

  return (
    <Field label={label} htmlFor={id} required={required} help={help} error={error} width={width}>
      {searchable ? (
        <Select
          inputId={id}
          className="react-select-container"
          classNamePrefix="react-select"
          options={options}
          value={selected}
          isClearable={!required}
          required={required}
          isLoading={loading}
          isDisabled={disabled}
          placeholder={placeholder ?? t`Select…`}
          aria-invalid={!!error}
          aria-errormessage={error ? `${id}-error` : undefined}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          styles={{ menuPortal: base => ({ ...base, zIndex: 1060 }) }}
          onChange={option => onChange(option ? (option as SelectOption).value : null)}
        />
      ) : (
        <Input
          id={id}
          type="select"
          value={value === null || value === undefined ? '' : String(value)}
          disabled={disabled || loading}
          invalid={!!error}
          aria-invalid={!!error}
          aria-required={required}
          aria-describedby={describedById(id, error, help)}
          onChange={e => {
            const match = options.find(option => String(option.value) === e.target.value);
            onChange(match ? match.value : null);
          }}
        >
          <option value="">{loading ? t`Loading…` : placeholder ?? t`Select…`}</option>
          {options.map(option => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </Input>
      )}
    </Field>
  );
}

export default SelectField;
